import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Phone, Mail, MapPin, Clock, Facebook, Twitter, Youtube, Linkedin, Instagram, ArrowRight, Printer } from 'lucide-react';
import { locations, contactInfo } from '../data/mock';

const Footer = () => {
  const location = useLocation();
  const isServicesActive = location.pathname === '/' && (!location.hash || location.hash === '#services');
  const isBlogActive = location.pathname === '/blog' || location.pathname.startsWith('/blog/');
  const isAttorneyActive = location.pathname === '/for-attorneys';
  const isChiroActive = location.pathname === '/for-chiropractors';

  const navItemClass = (isActive, hoverClass = 'hover:text-[#20B2AA]') =>
    `${isActive ? 'text-[#20B2AA] font-semibold' : ''} ${hoverClass} transition-colors flex items-center gap-2`;

  return (
    <footer className="bg-[#1B1B3A] text-gray-300">
      <div className="container mx-auto px-4 py-16">
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-12 mb-12">
          {/* Company Info */}
          <div>
            <div className="mb-6">
              <img 
                src="https://customer-assets.emergentagent.com/job_e50feeb1-fb0e-41c1-8efc-bccb0f886d1e/artifacts/lg1k57g2_Untitled%20design%20%2812%29.png" 
                alt="ATLREGEN" 
                className="h-28 md:h-36 lg:h-44 w-auto object-contain brightness-0 invert"
              />
            </div>
            <p className="text-gray-400 mb-6 leading-relaxed">
              Board certified physicians specializing in non-surgical regenerative medicine since 2011.
            </p>
            <div className="flex gap-3">
              <a
                href="https://www.facebook.com/profile.php?id=61589178535431"
                target="_blank"
                rel="noopener noreferrer"
                className="bg-[#2D2D4A] p-3 rounded-lg hover:bg-[#E63946] transition-colors"
                aria-label="Facebook"
              >
                <Facebook className="h-5 w-5" />
              </a>
              <a
                href="https://x.com/atlregen_"
                target="_blank"
                rel="noopener noreferrer"
                className="bg-[#2D2D4A] p-3 rounded-lg hover:bg-[#E63946] transition-colors"
                aria-label="Twitter"
              >
                <Twitter className="h-5 w-5" />
              </a>
              <a
                href="https://www.youtube.com/@Atlregen"
                target="_blank"
                rel="noopener noreferrer"
                className="bg-[#2D2D4A] p-3 rounded-lg hover:bg-[#E63946] transition-colors"
                aria-label="YouTube"
              >
                <Youtube className="h-5 w-5" />
              </a>
              <a
                href="https://www.linkedin.com/in/carroll-cooper-10b3025"
                target="_blank"
                rel="noopener noreferrer"
                className="bg-[#2D2D4A] p-3 rounded-lg hover:bg-[#E63946] transition-colors"
                aria-label="LinkedIn"
              >
                <Linkedin className="h-5 w-5" />
              </a>
              <a
                href="https://www.instagram.com/atlregen/"
                target="_blank"
                rel="noopener noreferrer"
                className="bg-[#2D2D4A] p-3 rounded-lg hover:bg-[#E63946] transition-colors"
                aria-label="Instagram"
              >
                <Instagram className="h-5 w-5" />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-white font-bold text-lg mb-6">Services</h3>
            <ul className="space-y-3">
              <li><a href="/#services" className={navItemClass(false)}>
                <ArrowRight className="h-4 w-4" /> Regenerative Medicine
              </a></li>
              <li><a href="/#services" className={navItemClass(false)}>
                <ArrowRight className="h-4 w-4" /> Interventional Spine
              </a></li>
              <li><a href="/#services" className={navItemClass(false)}>
                <ArrowRight className="h-4 w-4" /> Orthopedic Care
              </a></li>
              <li><a href="/#services" className={navItemClass(false)}>
                <ArrowRight className="h-4 w-4" /> Personal Injury
              </a></li>
            </ul>
            
            <h3 className="text-white font-bold text-lg mb-4 mt-8">Referrals</h3>
            <ul className="space-y-3">
              <li><Link to="/for-attorneys" className={navItemClass(isAttorneyActive, 'hover:text-[#E63946]')}>
                <ArrowRight className="h-4 w-4" /> For Attorneys
              </Link></li>
              <li><Link to="/for-chiropractors" className={navItemClass(isChiroActive)}>
                <ArrowRight className="h-4 w-4" /> For Chiropractors
              </Link></li>
              <li><Link to="/blog" className={navItemClass(isBlogActive)}>
                <ArrowRight className="h-4 w-4" /> Blog
              </Link></li>
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h3 className="text-white font-bold text-lg mb-6">Contact Us</h3>
            <ul className="space-y-4">
              <li className="flex items-start gap-3">
                <Phone className="h-5 w-5 text-[#20B2AA] flex-shrink-0 mt-1" />
                <div>
                  <a href={`tel:${contactInfo.phone}`} className="hover:text-[#20B2AA] transition-colors font-medium">
                    {contactInfo.phone}
                  </a>
                  <p className="text-sm text-gray-500">{contactInfo.emergencyText}</p>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <Printer className="h-5 w-5 text-[#20B2AA] flex-shrink-0 mt-1" />
                <div>
                  <span className="font-medium">Fax: {contactInfo.fax}</span>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <Mail className="h-5 w-5 text-[#20B2AA] flex-shrink-0 mt-1" />
                <a href="/contact" className="hover:text-[#20B2AA] transition-colors">
                  Contact Us
                </a>
              </li>
              <li className="flex items-start gap-3">
                <Clock className="h-5 w-5 text-[#20B2AA] flex-shrink-0 mt-1" />
                <span>{contactInfo.hours}</span>
              </li>
            </ul>
          </div>

          {/* Locations */}
          <div>
            <h3 className="text-white font-bold text-lg mb-6">Our Locations</h3>
            <ul className="space-y-3">
              {locations.slice(0, 6).map((location) => (
                <li key={location.id} className="flex items-start gap-2">
                  <MapPin className="h-4 w-4 text-[#20B2AA] flex-shrink-0 mt-1" />
                  <a 
                    href={location.mapLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm hover:text-[#20B2AA] transition-colors"
                  >
                    {location.name}, {location.city.split(',')[1]}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-gray-700 pt-8 mt-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-sm text-gray-500">
              © 2026 ATLREGEN. All Rights Reserved.
            </p>
            <div className="flex gap-6 text-sm">
              <a href="/privacy-policy" className="hover:text-[#20B2AA] transition-colors">Privacy Policy</a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
